<template>
  <teleport to="body">
    <transition name="pam-fade">
      <div v-if="open" class="pam-overlay" @click.self="$emit('close')">
        <div class="pam-shell">

          <div class="pam-header">
            <div class="pam-header-left">
              <i class="fa-duotone fa-microchip-ai pam-header-icon"></i>
              <div>
                <div class="pam-header-title">swiftAI Application Analysis</div>
                <div class="pam-header-sub">Application review and opportunity snapshot</div>
              </div>
            </div>
            <div class="pam-header-right">
      
              <button class="pam-close-btn" @click="$emit('close')">
                <i class="fa-duotone fa-xmark"></i>
              </button>
            </div>
          </div>

          <div class="pam-body" v-if="analysis">

            <div class="pam-summary-section" v-if="summary">
              <div class="pam-section-label">Pipeline Summary</div>

              <div class="pam-risks-wrap" v-if="summary.keyRisks?.length">
                <div class="pam-sub-label">Key Risks</div>
                <ul class="pam-risk-list">
                  <li v-for="(risk, i) in summary.keyRisks" :key="i" class="pam-risk-item">
                    <i class="fa-duotone fa-triangle-exclamation pam-risk-ico"></i>
                    {{ risk }}
                  </li>
                </ul>
              </div>
            </div>

            <div class="pam-divider" v-if="borrowerCards?.length" />

            <div class="pam-cards-section" v-if="borrowerCards?.length">
              <div class="pam-section-label">Borrower Review Cards</div>

              <div class="pam-borrower-card" v-for="(card, i) in borrowerCards" :key="i">
                <div class="pam-bc-top">
                  <div class="pam-bc-top-left">
                    <div class="pam-bc-name">{{ card.displayName || card.borrowerName }}</div>
                    <div class="pam-bc-lo" v-if="card.loanOfficerName">
                      Loan Officer: {{ card.loanOfficerName }}
                    </div>
                  </div>
                  <div class="pam-bc-top-right">
                    <span class="pam-priority-pill" :data-priority="card.priority">
                      Priority: {{ card.priority }}
                    </span>
                    <div class="pam-approval-ring" :data-band="scoreBand(card.approvalProbability)">
                      <svg viewBox="0 0 36 36" class="pam-ring-svg">
                        <circle cx="18" cy="18" r="15.9" fill="none" stroke-width="3.2" class="pam-ring-bg" />
                        <circle
                          cx="18" cy="18" r="15.9" fill="none" stroke-width="3.2"
                          class="pam-ring-fill"
                          :stroke-dasharray="`${card.approvalProbability} ${100 - card.approvalProbability}`"
                          stroke-dashoffset="25"
                        />
                      </svg>
                      <span class="pam-ring-label">{{ card.approvalProbability }}%</span>
                    </div>
                  </div>
                </div>

                <div class="pam-bc-meta">
                  <div class="pam-bc-meta-group">
                    <div class="pam-bc-meta-label">LOAN PURPOSE</div>
                    <div class="pam-bc-meta-value">{{ card.loanPurpose || '—' }}</div>
                  </div>
                  <div class="pam-bc-meta-group">
                    <div class="pam-bc-meta-label">ESTIMATED LOAN</div>
                    <div class="pam-bc-meta-value">{{ formatMoney(card.estimatedLoanAmount) }}</div>
                  </div>
                  <div class="pam-bc-meta-group" v-if="card.propertyState">
                    <div class="pam-bc-meta-label">PROPERTY</div>
                    <div class="pam-bc-meta-value">{{ [card.propertyCity, card.propertyState].filter(Boolean).join(', ') }}</div>
                  </div>
                  <div class="pam-bc-meta-group" v-if="card.occupancy">
                    <div class="pam-bc-meta-label">OCCUPANCY</div>
                    <div class="pam-bc-meta-value">{{ card.occupancy }}</div>
                  </div>
                </div>

                <div class="pam-bc-block" v-if="card.summary">
                  <div class="pam-bc-block-label">SUMMARY</div>
                  <div class="pam-bc-block-text">{{ card.summary }}</div>
                </div>

                <div class="pam-bc-block" v-if="card.riskFlags?.length">
                  <div class="pam-bc-block-label">RISK FLAGS</div>
                  <div class="pam-flag-row">
                    <span class="pam-flag-pill" v-for="(flag, fi) in card.riskFlags" :key="fi">
                      {{ formatFlag(flag) }}
                    </span>
                  </div>
                </div>

                <div class="pam-bc-block" v-if="card.actionItems?.length">
                  <div class="pam-bc-block-label">RECOMMENDED ACTION ITEMS</div>
                  <ul class="pam-action-list">
                    <li v-for="(action, ai) in card.actionItems" :key="ai">{{ action }}</li>
                  </ul>
                </div>
              </div>
            </div>

            <div class="pam-scoring-footer" v-if="scoringModel">
              <div class="pam-scoring-label">Scoring Model: {{ scoringModel.version }}</div>
              <div class="pam-scoring-bands">
                <span class="pam-band" data-band="strong">Strong {{ scoringModel.bands?.strong }}</span>
                <span class="pam-band" data-band="workable">Workable {{ scoringModel.bands?.workable }}</span>
                <span class="pam-band" data-band="caution">Caution {{ scoringModel.bands?.caution }}</span>
                <span class="pam-band" data-band="weak">Weak {{ scoringModel.bands?.weak }}</span>
              </div>
            </div>
          </div>

          <div class="pam-loading" v-else-if="loading">
            <i class="fa-solid fa-spinner-third fa-spin pam-loading-icon"></i>
            <span>Loading analysis…</span>
          </div>

          <div class="pam-empty" v-else>
              <i class="fa-duotone fa-microchip-ai pam-empty-icon"></i>
            <div class="pam-empty-title">No analysis available</div>
            <div class="pam-empty-sub">AI analysis has not been generated for this application yet.</div>
          </div>

        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
export default {
  name: "PipelineAnalysisModal",
  props: {
    open:     { type: Boolean,  default: false },
    loading:  { type: Boolean,  default: false },
    analysis: { type: Object,   default: null  }
  },
  emits: ["close"],
  computed: {
    summary()      { return this.analysis?.summary       ?? null },
    borrowerCards(){ return this.analysis?.borrowerCards ?? []   },
    scoringModel() { return this.analysis?.scoringModel  ?? null }
  },
  methods: {
    formatMoney(v) {
      const n = Number(v)
      if (!Number.isFinite(n) || n === 0) return "—"
      return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n)
    },
    formatFlag(flag) {
      return (flag || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
    },
    scoreBand(score) {
      if (score >= 80) return "strong"
      if (score >= 60) return "workable"
      if (score >= 35) return "caution"
      return "weak"
    }
  }
}
</script>

<style scoped>
.pam-overlay {
  position: fixed;
  inset: 0;
  z-index: 50000;
  background: rgba(10, 15, 30, 0.55);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.pam-shell {
  width: 100%;
  max-width: min(900px, calc(100vw - 48px));
  height: min(90vh, 860px);
  border-radius: 5px;
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 32px 80px rgba(10, 15, 30, 0.28);
}

.pam-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px;
  background: #0f1c36;
  flex: 0 0 auto;
}

.pam-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.pam-header-icon {
  font-size: 26px;
  --fa-primary-color: #7dd3fc;
  --fa-secondary-color: #fde68a;
  --fa-secondary-opacity: 1;
}

.pam-empty-icon  { font-size: 36px; color: rgba(15,23,42,0.2); }

.pam-header-title {
  font-size: 16px;
  font-weight: 900;
  color: #fff;
  letter-spacing: 0.1px;
}

.pam-header-sub {
  font-size: 12px;
  font-weight: 600;
  color: rgba(255,255,255,0.55);
  margin-top: 2px;
}

.pam-header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.pam-logo {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 18px;
  font-weight: 900;
}

.pam-logo-star {
  color: #fde68a;
  font-size: 20px;
}

.pam-logo-swift { color: #fff; }
.pam-logo-ai    { color: #7dd3fc; }

.pam-close-btn {
  border: none;
  background: rgba(255,255,255,0.1);
  color: rgba(255,255,255,0.7);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.12s;
}

.pam-close-btn:hover { background: rgba(255,255,255,0.2); color: #fff; }

.pam-body {
  flex: 1 1 0;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.pam-section-label {
  font-size: 11px;
  font-weight: 950;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: #0f1c36;
  margin-bottom: 14px;
}

.pam-sub-label {
  font-size: 10.5px;
  font-weight: 950;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: rgba(15,23,42,0.5);
  margin-bottom: 8px;
}

.pam-risks-wrap   { margin-bottom: 18px; }

.pam-risk-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: rgba(15,23,42,0.78);
  padding: 8px 10px;
  border-radius: 8px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
}

.pam-risk-ico { color: #f97316; font-size: 13px; margin-top: 1px; flex-shrink: 0; }

.pam-risk-list    { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 6px; }

.pam-divider {
  border: none;
  border-top: 1px solid #e7ebf2;
  margin: 20px 0;
}

.pam-borrower-card {
  border: 1px solid #e0e5ef;
  border-radius: 14px;
  padding: 18px 20px;
  margin-bottom: 16px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(15,23,42,0.05);
}

.pam-bc-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.pam-bc-name {
  font-size: 16px;
  font-weight: 900;
  color: #0f1c36;
}

.pam-bc-lo {
  font-size: 12px;
  font-weight: 600;
  color: rgba(15,23,42,0.55);
  margin-top: 3px;
}

.pam-bc-top-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.pam-priority-pill {
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 900;
  border: 1px solid transparent;
  text-transform: capitalize;
}

.pam-priority-pill[data-priority="strong"]   { background: #dcfce7; border-color: #bbf7d0; color: #15803d; }
.pam-priority-pill[data-priority="high"]     { background: #dcfce7; border-color: #bbf7d0; color: #15803d; }
.pam-priority-pill[data-priority="medium"]   { background: #fef9c3; border-color: #fef08a; color: #92400e; }
.pam-priority-pill[data-priority="workable"] { background: #fef9c3; border-color: #fef08a; color: #92400e; }
.pam-priority-pill[data-priority="caution"]  { background: #ffedd5; border-color: #fed7aa; color: #c2410c; }
.pam-priority-pill[data-priority="low"]      { background: #fee2e2; border-color: #fecaca; color: #b91c1c; }
.pam-priority-pill[data-priority="weak"]     { background: #fee2e2; border-color: #fecaca; color: #b91c1c; }
.pam-priority-pill[data-priority="ignore"]   { background: #f1f5f9; border-color: #e2e8f0; color: #64748b; }

.pam-approval-ring {
  position: relative;
  width: 52px;
  height: 52px;
  flex-shrink: 0;
}

.pam-ring-svg {
  width: 52px;
  height: 52px;
  transform: rotate(-90deg);
}

.pam-ring-bg   { stroke: #e7ebf2; }

.pam-ring-fill { transition: stroke-dasharray 0.4s ease; }

.pam-approval-ring[data-band="strong"]   .pam-ring-fill { stroke: #22c55e; }
.pam-approval-ring[data-band="workable"] .pam-ring-fill { stroke: #3b82f6; }
.pam-approval-ring[data-band="caution"]  .pam-ring-fill { stroke: #f59e0b; }
.pam-approval-ring[data-band="weak"]     .pam-ring-fill { stroke: #ef4444; }

.pam-ring-label {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 900;
  color: #0f1c36;
}

.pam-bc-meta {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  margin-bottom: 14px;
  padding-bottom: 14px;
  border-bottom: 1px solid #f0f3f8;
}

.pam-bc-meta-label {
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: rgba(15,23,42,0.45);
  margin-bottom: 3px;
}

.pam-bc-meta-value {
  font-size: 14px;
  font-weight: 800;
  color: #0f1c36;
}

.pam-bc-block { margin-bottom: 12px; }

.pam-bc-block-label {
  font-size: 10px;
  font-weight: 950;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: rgba(15,23,42,0.45);
  margin-bottom: 6px;
}

.pam-bc-block-text {
  font-size: 13px;
  font-weight: 600;
  color: rgba(15,23,42,0.78);
  line-height: 1.55;
}

.pam-flag-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.pam-flag-pill {
  font-size: 11.5px;
  font-weight: 800;
  padding: 4px 10px;
  border-radius: 999px;
  background: #fee2e2;
  border: 1px solid #fecaca;
  color: #b91c1c;
}

.pam-action-list {
  padding-left: 18px;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.pam-action-list li {
  font-size: 13px;
  font-weight: 600;
  color: rgba(15,23,42,0.78);
  line-height: 1.5;
}

.pam-scoring-footer {
  margin-top: 8px;
  padding: 12px 14px;
  border: 1px solid #e7ebf2;
  border-radius: 10px;
  background: #f8faff;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.pam-scoring-label {
  font-size: 11px;
  font-weight: 800;
  color: rgba(15,23,42,0.5);
}

.pam-scoring-bands { display: flex; gap: 8px; flex-wrap: wrap; }

.pam-band {
  font-size: 11px;
  font-weight: 800;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid transparent;
}

.pam-band[data-band="strong"]   { background: #dcfce7; border-color: #bbf7d0; color: #15803d; }
.pam-band[data-band="workable"] { background: #dbeafe; border-color: #bfdbfe; color: #1d4ed8; }
.pam-band[data-band="caution"]  { background: #fef9c3; border-color: #fef08a; color: #92400e; }
.pam-band[data-band="weak"]     { background: #fee2e2; border-color: #fecaca; color: #b91c1c; }

.pam-loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 48px;
  color: rgba(15,23,42,0.6);
  font-size: 14px;
  font-weight: 700;
}

.pam-loading-icon { font-size: 28px; color: #2563eb; }

.pam-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px;
  text-align: center;
}

.pam-empty-icon  { font-size: 36px; color: rgba(15,23,42,0.2); }
.pam-empty-title { font-size: 15px; font-weight: 900; color: rgba(15,23,42,0.5); }
.pam-empty-sub   { font-size: 13px; font-weight: 600; color: rgba(15,23,42,0.4); }

.pam-fade-enter-active,
.pam-fade-leave-active { transition: opacity 0.18s ease; }
.pam-fade-enter-from,
.pam-fade-leave-to { opacity: 0; }
</style>